import React, { useEffect, useMemo, useState } from "react";
import networkInstance from "../../services/api";
import toastMessage from "../../toast";

const CITY_OPTIONS = ["jaipur", "ncr", "delhi", "new delhi", "noida"];

const WEEKDAYS = [
  { label: "Sunday", short: "Sun", value: "sunday", index: 0 },
  { label: "Monday", short: "Mon", value: "monday", index: 1 },
  { label: "Tuesday", short: "Tue", value: "tuesday", index: 2 },
  { label: "Wednesday", short: "Wed", value: "wednesday", index: 3 },
  { label: "Thursday", short: "Thu", value: "thursday", index: 4 },
  { label: "Friday", short: "Fri", value: "friday", index: 5 },
  { label: "Saturday", short: "Sat", value: "saturday", index: 6 },
];

const WEEKDAY_VALUE_TO_INDEX = WEEKDAYS.reduce((acc, day) => {
  acc[day.value] = day.index;
  return acc;
}, {});

const PRESETS = [
  {
    id: "all",
    label: "All Days",
    days: WEEKDAYS.map((day) => day.value),
  },
  {
    id: "weekdays",
    label: "Weekdays",
    days: ["monday", "tuesday", "wednesday", "thursday", "friday"],
  },
  {
    id: "weekend",
    label: "Weekend",
    days: ["saturday", "sunday"],
  },
  {
    id: "jaipurDefault",
    label: "Jaipur Default",
    days: ["wednesday", "sunday"],
  },
  {
    id: "ncrDefault",
    label: "NCR Default",
    days: ["tuesday", "wednesday", "saturday"],
  },
];

const formatCityLabel = (city) =>
  city
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");

const getUpcomingDatesPreview = (allowedDays, count = 10) => {
  if (!allowedDays?.length) {
    return [];
  }

  const dayIndexes = new Set(
    allowedDays
      .map((day) => WEEKDAY_VALUE_TO_INDEX[day])
      .filter((index) => Number.isInteger(index))
  );

  if (dayIndexes.size === 0) {
    return [];
  }

  const result = [];
  const today = new Date();

  for (let offset = 0; offset < 90 && result.length < count; offset += 1) {
    const candidate = new Date(today);
    candidate.setDate(today.getDate() + offset);

    if (dayIndexes.has(candidate.getDay())) {
      result.push(candidate);
    }
  }

  return result;
};

const SlotManagmentPage = () => {
  const [selectedCity, setSelectedCity] = useState("jaipur");
  const [selectedDays, setSelectedDays] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [activePreset, setActivePreset] = useState("");

  const selectedDaySet = useMemo(() => new Set(selectedDays), [selectedDays]);
  const selectedDaysInWeekOrder = useMemo(() => {
    return [...selectedDays].sort(
      (a, b) => WEEKDAY_VALUE_TO_INDEX[a] - WEEKDAY_VALUE_TO_INDEX[b]
    );
  }, [selectedDays]);

  const upcomingDates = useMemo(
    () => getUpcomingDatesPreview(selectedDaysInWeekOrder, 10),
    [selectedDaysInWeekOrder]
  );

  const fetchCityConfig = async (city) => {
    setIsLoading(true);
    try {
      const { data } = await networkInstance.get(
        `admin/get_pickup_slot_config?city=${encodeURIComponent(city)}`
      );

      if (data?.code === 200) {
        const dayNames = Array.isArray(data?.data?.allowedWeekdayNames)
          ? data.data.allowedWeekdayNames
          : [];

        setSelectedDays(dayNames);
        setActivePreset("");
      } else {
        toastMessage("error", data?.message || "Failed to fetch slot config");
      }
    } catch (error) {
      toastMessage(
        "error",
        error?.response?.data?.message || error?.message || "Failed to fetch slot config"
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCityConfig(selectedCity);
  }, [selectedCity]);

  const toggleDay = (day) => {
    setSelectedDays((prev) => {
      if (prev.includes(day)) {
        setActivePreset("");
        return prev.filter((item) => item !== day);
      }
      setActivePreset("");
      return [...prev, day];
    });
  };

  const applyPreset = (preset) => {
    setSelectedDays(preset.days);
    setActivePreset(preset.id);
  };

  const handleSave = async () => {
    if (selectedDays.length === 0) {
      toastMessage("error", "Please select at least one day");
      return;
    }

    setIsSaving(true);

    try {
      const payload = {
        city: selectedCity,
        allowedWeekdays: selectedDays,
      };

      const { data } = await networkInstance.post(
        "admin/upsert_pickup_slot_config",
        payload
      );

      if (data?.code === 200) {
        const updatedDays = Array.isArray(data?.data?.allowedWeekdayNames)
          ? data.data.allowedWeekdayNames
          : selectedDays;
        setSelectedDays(updatedDays);
        toastMessage("success", "Slot rules saved successfully");
      } else {
        toastMessage("error", data?.message || "Failed to save slot config");
      }
    } catch (error) {
      toastMessage(
        "error",
        error?.response?.data?.message || error?.message || "Failed to save slot config"
      );
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="rounded-2xl border border-slate-200 bg-gradient-to-br from-white via-orange-50 to-amber-50 p-[24px] md:p-[30px] shadow-sm">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <h3 className="font-semibold text-[22px] text-slate-900">Slot Managment</h3>
          <p className="text-sm text-slate-600 mt-1">
            Configure pickup days city-wise with instant visual preview.
          </p>
        </div>

        <div className="rounded-xl bg-white border border-slate-200 px-4 py-3 min-w-[220px]">
          <p className="text-xs uppercase tracking-wide text-slate-500">Current City</p>
          <p className="text-lg font-semibold text-slate-900 mt-1">{formatCityLabel(selectedCity)}</p>
        </div>
      </div>

      <div className="mt-6">
        <p className="text-sm font-medium text-slate-900 mb-3">Choose City</p>
        <div className="flex flex-wrap gap-2">
          {CITY_OPTIONS.map((city) => {
            const isSelected = selectedCity === city;
            return (
              <button
                key={city}
                type="button"
                onClick={() => setSelectedCity(city)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                  isSelected
                    ? "bg-[#ff5028] text-white shadow-md"
                    : "bg-white text-slate-700 border border-slate-200 hover:border-[#ff5028] hover:text-[#ff5028]"
                }`}
              >
                {formatCityLabel(city)}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-6">
        <p className="text-sm font-medium text-slate-900 mb-3">Quick Presets</p>
        <div className="flex flex-wrap gap-2">
          {PRESETS.map((preset) => {
            const isSelected = activePreset === preset.id;
            return (
              <button
                key={preset.id}
                type="button"
                onClick={() => applyPreset(preset)}
                className={`px-3 py-2 rounded-lg text-xs md:text-sm font-medium transition-all duration-200 ${
                  isSelected
                    ? "bg-slate-900 text-white"
                    : "bg-white border border-slate-200 text-slate-700 hover:border-slate-500"
                }`}
              >
                {preset.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mt-6">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-slate-900">Allowed Pickup Days</h4>
          <span className="text-xs bg-white border border-slate-200 text-slate-700 px-2 py-1 rounded-md">
            {selectedDaysInWeekOrder.length} day{selectedDaysInWeekOrder.length === 1 ? "" : "s"} selected
          </span>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {Array.from({ length: 7 }).map((_, idx) => (
              <div
                key={`loading-${idx}`}
                className="h-[88px] rounded-xl border border-slate-200 bg-white animate-pulse"
              />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
            {WEEKDAYS.map((day) => {
              const isSelected = selectedDaySet.has(day.value);
              return (
                <button
                  key={day.value}
                  type="button"
                  onClick={() => toggleDay(day.value)}
                  className={`h-[88px] rounded-xl border text-left px-3 py-2 transition-all duration-200 ${
                    isSelected
                      ? "border-[#ff5028] bg-[#ff5028] text-white shadow-md -translate-y-[1px]"
                      : "border-slate-200 bg-white text-slate-700 hover:border-[#ff5028] hover:shadow-sm"
                  }`}
                >
                  <p className="text-xs opacity-80">{day.short}</p>
                  <p className="text-sm font-semibold mt-1">{day.label}</p>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="mt-7 rounded-xl border border-slate-200 bg-white p-4">
        <div className="flex items-center justify-between">
          <h5 className="text-sm font-semibold text-slate-900">Upcoming Pickup Dates Preview</h5>
          <span className="text-xs text-slate-500">Next 10 available</span>
        </div>

        {upcomingDates.length === 0 ? (
          <p className="text-sm text-red-500 mt-3">Select at least one day to generate preview.</p>
        ) : (
          <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-2">
            {upcomingDates.map((date, index) => (
              <div
                key={`${date.toISOString()}-${index}`}
                className="rounded-lg border border-slate-200 px-3 py-2 bg-slate-50"
              >
                <p className="text-xs text-slate-500">
                  {date.toLocaleDateString("en-US", { weekday: "short" })}
                </p>
                <p className="text-sm font-semibold text-slate-900">
                  {date.toLocaleDateString("en-US", {
                    day: "2-digit",
                    month: "short",
                  })}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-8 flex flex-wrap gap-3 items-center">
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving || isLoading}
          className="text-white bg-[#ff5028] hover:bg-[#ff5028cf] font-medium rounded-lg text-sm px-5 py-2.5 text-center disabled:opacity-60"
        >
          {isSaving ? "Saving..." : "Save Slot Rules"}
        </button>

        <button
          type="button"
          onClick={() => {
            setSelectedDays([]);
            setActivePreset("");
          }}
          className="text-slate-700 bg-white border border-slate-300 hover:border-slate-500 font-medium rounded-lg text-sm px-5 py-2.5 text-center"
        >
          Clear Selection
        </button>
      </div>
    </div>
  );
};

export default SlotManagmentPage;
